/**
 * @fileoverview Scroll-triggered text animation component that reveals characters progressively.
 * Creates a reading effect where text opacity increases based on scroll position.
 */

import { motion, useScroll, useTransform } from 'motion/react';
import { useRef } from 'react';
import type { ParagraphProps, WordProps, CharProps } from '../types/components';

/**
 * Main paragraph component with scroll-triggered character opacity animation.
 *
 * @description Creates a text reveal effect where each character's opacity increases
 * as the user scrolls through the paragraph. Characters appear progressively from
 * low opacity to full opacity, creating an engaging reading experience.
 *
 * @param {ParagraphProps} props - Component properties
 * @param {string} props.paragraph - Text content to animate
 * @param {string} [props.className] - Additional CSS classes for styling
 *
 * @returns {JSX.Element} Animated paragraph component
 *
 * @example
 * ```tsx
 * <Paragraph
 *   paragraph="This text will reveal character by character as you scroll"
 *   className="text-2xl font-light text-gray-800"
 * />
 * ```
 */
export default function Paragraph({ paragraph, className }: ParagraphProps) {
  /** Container reference for scroll tracking */
  const container = useRef(null);

  /**
   * Scroll progress tracking for the paragraph container.
   * Animation starts when paragraph reaches 90% of viewport height,
   * completes when it reaches 25% of viewport height.
   */
  const { scrollYProgress } = useScroll({
    target: container,
    offset: ['start 0.9', 'start 0.25'], // Animation trigger points
  });

  /** Split paragraph into individual words for progressive animation */
  const words = paragraph.split(' ');

  return (
    <p ref={container} className={`flex p-10 flex-wrap ${className}`}>
      {words.map((word: string, i: number) => {
        // Calculate animation range for each word based on its position
        const start = i / words.length;
        const end = start + 1 / words.length;
        return (
          <Word key={i} progress={scrollYProgress} range={[start, end]}>
            {word}
          </Word>
        );
      })}
    </p>
  );
}

/**
 * Word component that handles character-by-character animation within a word.
 *
 * @description Splits a word into individual characters and applies progressive
 * opacity animation based on scroll position and the word's position in the text.
 *
 * @param {WordProps} props - Component properties
 * @param {string} props.children - Word content to animate
 * @param {MotionValue} props.progress - Scroll progress from parent
 * @param {[number, number]} props.range - Animation range for this word
 *
 * @returns {JSX.Element} Animated word component
 */
const Word = ({ children, progress, range }: WordProps) => {
  /** Calculate animation step size for each character in the word */
  const amount = range[1] - range[0];
  const step = amount / children.length;

  return (
    <span className="relative mr-3 mt-3">
      {children.split('').map((char, i) => {
        // Calculate individual character animation range
        const start = range[0] + i * step;
        const end = range[0] + (i + 1) * step;
        return (
          <Char key={`c_${i}`} progress={progress} range={[start, end]}>
            {char}
          </Char>
        );
      })}
    </span>
  );
};

/**
 * Individual character component with opacity animation.
 *
 * @description Creates a layered character effect with a low-opacity background
 * character and a fully animated foreground character that fades in based on
 * scroll position.
 *
 * @param {CharProps} props - Component properties
 * @param {string} props.children - Character to animate
 * @param {MotionValue} props.progress - Scroll progress from parent
 * @param {[number, number]} props.range - Animation range for this character
 *
 * @returns {JSX.Element} Animated character component
 */
const Char = ({ children, progress, range }: CharProps) => {
  /** Transform scroll progress to opacity value for this character */
  const opacity = useTransform(progress, range, [0, 1]);

  return (
    <span>
      {/* Background character with low opacity - always visible */}
      <span className="absolute opacity-15">{children}</span>

      {/* Foreground character with animated opacity */}
      <motion.span style={{ opacity: opacity }}>{children}</motion.span>
    </span>
  );
};
